import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SalesforceFields,
  salesforceOpportunityFieldsQueryKeys,
} from '@/hooks/salesforce/types'
import { useHasSalesforceIntegration } from '@/hooks/salesforce/useSalesforceIntegration'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseSalesforceOpportunityFieldsArgs<T> {
  orgId: UUID | ''
  enabled: boolean
  select?: (data: SalesforceFields) => T
}

export const fetchSalesforceOpportunityFields = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<SalesforceFields> => {
  if (!orgId) throw new Error('No organization ID provided.')
  const url = `${API_URL}/api/v1/organizations/${orgId}/salesforce/fields/Opportunity`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as SalesforceFields
}

export const useSalesforceOpportunityFields = <T = SalesforceFields>({
  orgId,
  enabled,
  select,
}: UseSalesforceOpportunityFieldsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: SalesforceFields) => {
      if (select) {
        return select(data) as unknown as T
      }
      return data as unknown as T
    },
    [select],
  )
  return useQuery({
    queryKey: salesforceOpportunityFieldsQueryKeys.organization({ orgId }),
    queryFn: () => fetchSalesforceOpportunityFields(orgId, accessToken),
    enabled: !!orgId && enabled,
    staleTime: Infinity,
    select: selectFn,
  })
}

interface UseHasSalesforceValidationRulesArgs {
  orgId: UUID | ''
}

const selectHasSalesforceValidationRules = (data: SalesforceFields) => {
  return data.validationRules.length > 0
}

export const useHasSalesforceValidationRules = ({
  orgId,
}: UseHasSalesforceValidationRulesArgs) => {
  const { data: hasSalesforceIntegration = false } =
    useHasSalesforceIntegration({ orgId })
  return useSalesforceOpportunityFields({
    orgId,
    select: selectHasSalesforceValidationRules,
    enabled: hasSalesforceIntegration,
  })
}
